import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Check } from "lucide-react";
import PicImg from "../PicImg";

/**
 * Product Final CTA — full-width cinematic section with a real site
 * photograph background, headline, primary + secondary CTAs and trust
 * badges strip.
 *
 * Props:
 *  - testId
 *  - backgroundImage: string  (public path)
 *  - backgroundAlt: string
 *  - eyebrow (default "Let's Build Together")
 *  - heading (ReactNode)
 *  - subtitle
 *  - primaryCta: { label, href }
 *  - secondaryCta: { label, href }
 *  - trustBadges: string[]
 */
export default function ProductFinalCTA({
  testId,
  backgroundImage,
  backgroundAlt,
  eyebrow = "Let's Build Together",
  heading,
  subtitle,
  primaryCta,
  secondaryCta,
  trustBadges = [],
  overlayGradient = "bg-gradient-to-b from-liftile-black/88 via-liftile-black/78 to-liftile-black/95",
}) {
  return (
    <section
      data-testid={testId}
      className="relative overflow-hidden py-28 md:py-40 lg:py-48"
    >
      <div className="absolute inset-0">
        <PicImg
          src={backgroundImage}
          alt={backgroundAlt}
          className="w-full h-full object-cover" loading="lazy" decoding="async" />
        <div className={`absolute inset-0 ${overlayGradient}`} />
        <div className="absolute inset-0 bg-gradient-to-tr from-liftile-orange/12 via-transparent to-transparent" />
      </div>

      <div className="container-x relative">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.75, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-3xl mx-auto text-center"
        >
          <div className="mx-auto w-14 h-px bg-liftile-orange mb-7" />
          <div className="heading-eyebrow mb-5">{eyebrow}</div>
          <h2
            data-testid={`${testId}-heading`}
            className="font-display uppercase text-liftile-cream leading-[1.02] tracking-tight text-4xl sm:text-5xl md:text-6xl lg:text-[4.25rem]"
          >
            {heading}
          </h2>
          {subtitle && (
            <p className="font-body text-liftile-cream/75 mt-7 text-base md:text-lg leading-relaxed max-w-2xl mx-auto">
              {subtitle}
            </p>
          )}

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ delay: 0.2, duration: 0.65 }}
            className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4"
          >
            {primaryCta && (
              <Link
                to={primaryCta.href}
                data-testid={`${testId}-quote-btn`}
                className="group inline-flex items-center justify-center gap-3 bg-liftile-orange text-white font-heading font-semibold uppercase tracking-wider text-xs md:text-sm px-9 py-[17px] rounded-md hover:-translate-y-0.5 hover:shadow-[0_18px_50px_-12px_rgba(255,107,26,0.85)] transition-all duration-400"
              >
                {primaryCta.label}
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </Link>
            )}
            {secondaryCta && (
              <Link
                to={secondaryCta.href}
                data-testid={`${testId}-secondary-btn`}
                className="inline-flex items-center justify-center gap-2 px-9 py-[17px] border border-liftile-cream/30 text-liftile-cream font-heading font-semibold uppercase tracking-wider text-xs md:text-sm rounded-md hover:bg-liftile-cream hover:text-liftile-matte-black transition-all duration-300"
              >
                {secondaryCta.label}
              </Link>
            )}
          </motion.div>

          {trustBadges.length > 0 && (
            <motion.ul
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ delay: 0.35, duration: 0.7 }}
              data-testid={`${testId}-trust-badges`}
              className="mt-12 flex flex-wrap items-center justify-center gap-x-8 gap-y-3"
            >
              {trustBadges.map((b) => (
                <li
                  key={b}
                  className="inline-flex items-center gap-2 font-heading text-[10px] md:text-[11px] uppercase tracking-[0.22em] text-liftile-cream/80 font-semibold"
                >
                  <Check
                    className="w-3.5 h-3.5 text-liftile-orange"
                    strokeWidth={3}
                  />
                  {b}
                </li>
              ))}
            </motion.ul>
          )}
        </motion.div>
      </div>
    </section>
  );
}
